import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import type { BreakdownResult } from '~/pages/BalanceGenerationDetail/models/convertGenerationDetailResponse'

type Props = {
  breakdown: BreakdownResult
}

// PLANT_COLORS is a fixed 5-color rotation applied by array index (発電所別内訳の
// 行数は BG 配下の発電所数に応じて可変なため、発電形式のような固定キー辞書では
// なく index base で割り当てる)。
const PLANT_COLORS: readonly string[] = [
  LightModeSemanticColors.Text.negativeMid.value,
  LightModeSemanticColors.Brand.yellow.value,
  LightModeSemanticColors.Text.positiveMid.value,
  '#3b82f6',
  LightModeSemanticColors.Brand.purple.value,
]

export const BreakdownPanel = ({ breakdown }: Props): React.ReactElement => {
  const { t } = useTranslation()

  const colorFor = (index: number): string =>
    PLANT_COLORS[index % PLANT_COLORS.length] ??
    LightModeSemanticColors.Text.highEmphasis.value

  return (
    <Flex
      flexDirection="column"
      gap="6px"
      overflowY="auto"
      minHeight={0}
      padding="10px"
      /* パネルは白 (`Surface.primary`)。 design
         (`js/hatsu-detail-data.js:197-205`) はこのパネルを `.card` で出しており、
         `.card` の地は `--surface-primary`。 同じ画面の他カードも白なので、
         ここだけ灰だと 1 枚だけ沈んで見えていた (#7064)。 見出し帯は背景を
         持たず親を継承するので、 この 1 行で両方が白になる。 */
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
    >
      {/* Title */}
      {/* fontSize (sm) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
      <div
        style={{
          fontSize: fontSizes.sm.value,
          fontWeight: 700,
          color: LightModeSemanticColors.Text.highEmphasis.value,
          marginBottom: '4px',
          paddingBottom: '4px',
          borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
        }}
      >
        {t('page:balanceGenerationDetail.breakdown.title')}
      </div>

      {/* Source rows (発電所別内訳) */}
      <Flex flexDirection="column" gap="4px">
        {breakdown.sources.map((s, i) => (
          <Flex
            key={s.key}
            alignItems="baseline"
            justifyContent="space-between"
            gap="8px"
            // design `css/balance.css:444` `.bkd-row` = `--font-size-md` (14px)。
            // 2026-09-07 以前は sm (12px) で 1 段小さかった (issue #7064)。
            fontSize="md"
          >
            {/* 色を持つのは **■ swatch だけ**。 design `js/hatsu-detail-data.js:192` は
                `<span class="bkd-swatch" style="color:…">■</span> ラベル` の形で、
                `.bkd-label` (balance.css:445) は色を指定していない (= 地の文字色を継承)。
                2026-09-07 以前は実装がラベル文字**全体**を系列色で塗っており、
                特に 4 行目の色はリンク色に近く**押せる文字に見えていた** (issue #7064)。 */}
            <Text
              as="span"
              flex={1}
              overflow="hidden"
              minWidth={0}
              fontWeight="semibold"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              <Box as="span" color={colorFor(i)}>
                ■
              </Box>{' '}
              {s.label}
            </Text>
            <Text
              as="span"
              color="Text.highEmphasis"
              fontVariantNumeric="tabular-nums"
            >
              {Math.round(s.value).toLocaleString()}{' '}
              {t('page:balanceGenerationDetail.breakdown.kwh')}
            </Text>
            <Text
              as="span"
              width="48px"
              color="Text.midEmphasis"
              textAlign="right"
              fontVariantNumeric="tabular-nums"
            >
              {s.pct.toFixed(1)}%
            </Text>
          </Flex>
        ))}
      </Flex>

      {/* Total */}
      {/* design `css/balance.css:448` `.bkd-total` は font-size を上書きせず
          `.bkd-row` の `--font-size-md` (14px) を継承する。 2026-09-07 以前は
          sm (12px) だった (issue #7064)。
          fontSize (md) + fontWeight (700) の組に一致する役割 typography が無いため raw 維持。 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          fontSize: fontSizes.md.value,
          gap: '8px',
          marginTop: '6px',
          paddingTop: '6px',
          borderTop: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
          fontWeight: 700,
        }}
      >
        <Box as="span" flex={1}>
          {t('page:balanceGenerationDetail.breakdown.total')}
        </Box>
        <Text
          as="span"
          color="Text.highEmphasis"
          fontVariantNumeric="tabular-nums"
        >
          {Math.round(breakdown.total).toLocaleString()}{' '}
          {t('page:balanceGenerationDetail.breakdown.kwh')}
        </Text>
        <Text
          as="span"
          width="48px"
          color="Text.midEmphasis"
          textAlign="right"
          fontVariantNumeric="tabular-nums"
        >
          100%
        </Text>
      </div>

      {/* Bar */}
      <Flex
        overflow="hidden"
        height="14px"
        marginTop="6px"
        background="Surface.quaternary"
        // borderRadius (7px) が radius token (sm=2/base=4/md=6/lg=8) と一致しないため
        // 残余 style として維持。
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        style={{ borderRadius: '7px' }}
      >
        {breakdown.sources.map((s, i) => (
          <HintTooltip key={s.key} content={`${s.label} ${s.pct.toFixed(1)}%`}>
            <Box
              width={`${s.pct.toFixed(2)}%`}
              height="100%"
              background={colorFor(i)}
              title={`${s.label} ${s.pct.toFixed(1)}%`}
            />
          </HintTooltip>
        ))}
      </Flex>
    </Flex>
  )
}
